import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { getEmployeeById, type EmployeeRecord } from "@/lib/hr/employeesStore";
import { createEmployeeLoan, loansApiErrorMessage } from "@/lib/hr/loansStore";
import { addMonths, format, isValid, parseISO } from "date-fns";
import { Calculator, Calendar, CalendarRange, CheckCircle2, Coins, HandCoins, Info, Loader2, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type Draft = {
  loanAmount: string;
  installmentAmount: string;
  startDate: string;
  reason: string;
};

const emptyDraft = (): Draft => ({
  loanAmount: "",
  installmentAmount: "",
  startDate: format(new Date(), "yyyy-MM-dd"),
  reason: "",
});

function safeNumber(v: string | number | null | undefined): number | null {
  if (v == null) return null;
  const raw = String(v).replace(/[^\d.]/g, "");
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function commaFormat(n: number | null | undefined): string {
  if (n == null) return "0";
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);
}

export type EmployeeLoanCreateDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string;
  onSaved?: () => void;
};

export function EmployeeLoanCreateDialog({ open, onOpenChange, employeeId, onSaved }: EmployeeLoanCreateDialogProps) {
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [employee, setEmployee] = useState<EmployeeRecord | undefined>(() => getEmployeeById(employeeId));

  useEffect(() => {
    if (open) {
      setDraft(emptyDraft());
      setEmployee(getEmployeeById(employeeId));
    }
  }, [open, employeeId]);

  const loanNum = useMemo(() => safeNumber(draft.loanAmount), [draft.loanAmount]);
  const instNum = useMemo(() => safeNumber(draft.installmentAmount), [draft.installmentAmount]);

  const installmentsCount = useMemo(() => {
    if (!loanNum || !instNum) return null;
    return Math.max(1, Math.ceil(loanNum / instNum));
  }, [loanNum, instNum]);

  const completionDateFormatted = useMemo(() => {
    if (!installmentsCount || !draft.startDate.trim()) return null;
    const dt = parseISO(draft.startDate);
    if (!isValid(dt)) return null;
    const endDt = addMonths(dt, installmentsCount);
    return format(endDt, "MMM yyyy");
  }, [installmentsCount, draft.startDate]);

  const applyTenurePreset = (months: number) => {
    if (!loanNum) {
      toast.error("Please enter a loan amount first.");
      return;
    }
    const inst = Math.round(loanNum / months);
    setDraft((prev) => ({ ...prev, installmentAmount: String(inst) }));
  };

  const applyAmountPreset = (amt: number) => {
    setDraft((prev) => {
      const nextLoan = String(amt);
      const nextInst = prev.installmentAmount
        ? String(Math.round(amt / (safeNumber(prev.installmentAmount) ? Math.ceil(safeNumber(prev.loanAmount) || amt) : 6)))
        : String(Math.round(amt / 6));
      return {
        ...prev,
        loanAmount: nextLoan,
        installmentAmount: nextInst,
      };
    });
  };

  const close = () => onOpenChange(false);

  const save = async () => {
    if (!employeeId || saving) return;
    if (!loanNum || loanNum <= 0) return toast.error("Loan amount must be a number > 0.");
    if (!instNum || instNum <= 0) return toast.error("Installment amount must be a number > 0.");
    if (!draft.startDate.trim()) return toast.error("Start date is required.");

    setSaving(true);
    try {
      const result = await createEmployeeLoan({
        employeeId,
        loanAmount: String(loanNum),
        installmentAmount: String(instNum),
        startDate: draft.startDate,
      });
      toast.success(result.message);
      close();
      onSaved?.();
    } catch (e) {
      toast.error(loansApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const basicSalaryNum = employee?.basicSalary ? Number(employee.basicSalary) : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto p-6">
        <DialogHeader className="pb-3 border-b border-border/50">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-600">
              <HandCoins className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-foreground">Issue Employee Loan</DialogTitle>
              <p className="text-xs text-muted-foreground">
                Sanction a company loan with automated monthly payroll installment deductions.
              </p>
            </div>
          </div>
        </DialogHeader>

        {/* Employee Summary Bar */}
        {employee ? (
          <div className="flex items-center justify-between bg-muted/30 border border-border/50 rounded-xl p-3 text-xs">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center font-bold text-primary text-xs">
                {employee.firstName ? employee.firstName[0] : "E"}
              </div>
              <div>
                <p className="font-bold text-foreground">{employee.fullName || `${employee.firstName} ${employee.lastName}`}</p>
                <p className="text-[11px] text-muted-foreground">{employee.designationName || "Staff"} · {employee.employeeCode || "—"}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase font-semibold text-muted-foreground">Basic Monthly Salary</p>
              <p className="font-bold text-primary">{formatCurrency(basicSalaryNum)}</p>
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
          {/* Left Column: Form Inputs */}
          <div className="space-y-4">
            {/* Sanctioned Loan Amount */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-foreground flex justify-between items-center">
                <span>Sanctioned Loan Amount <span className="text-destructive">*</span></span>
                {loanNum ? <span className="text-xs text-emerald-600 font-bold">Rs. {commaFormat(loanNum)}</span> : null}
              </Label>
              <div className="relative">
                <Input
                  className="glass-input h-10 pr-12 font-semibold"
                  value={draft.loanAmount}
                  onChange={(e) => setDraft((d) => ({ ...d, loanAmount: e.target.value }))}
                  placeholder="e.g. 50,000"
                  inputMode="decimal"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
                  PKR
                </span>
              </div>

              {/* Quick Amount Presets */}
              <div className="flex flex-wrap gap-1.5 pt-1">
                <span className="text-[10px] text-muted-foreground self-center mr-1">Quick:</span>
                {[25000, 50000, 100000].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => applyAmountPreset(preset)}
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-border/60 bg-background hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-colors"
                  >
                    +{commaFormat(preset)}
                  </button>
                ))}
                {basicSalaryNum > 0 ? (
                  <button
                    type="button"
                    onClick={() => applyAmountPreset(basicSalaryNum)}
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-primary/30 bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                  >
                    1x Salary
                  </button>
                ) : null}
              </div>
            </div>

            {/* Monthly Installment Amount */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-foreground flex justify-between items-center">
                <span>Monthly Installment <span className="text-destructive">*</span></span>
                {instNum ? <span className="text-xs text-rose-600 font-bold">-{formatCurrency(instNum)}/mo</span> : null}
              </Label>
              <div className="relative">
                <Input
                  className="glass-input h-10 pr-12 font-semibold"
                  value={draft.installmentAmount}
                  onChange={(e) => setDraft((d) => ({ ...d, installmentAmount: e.target.value }))}
                  placeholder="e.g. 5,000"
                  inputMode="decimal"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
                  PKR
                </span>
              </div>

              {/* Quick Tenure Presets */}
              <div className="flex flex-wrap items-center gap-1.5 pt-1">
                <span className="text-[10px] text-muted-foreground self-center mr-1">Tenure:</span>
                {[3, 6, 12, 24].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => applyTenurePreset(m)}
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-border/60 bg-background hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-colors"
                  >
                    {m} Mo
                  </button>
                ))}
              </div>
            </div>

            {/* Deduction Start Date */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-foreground">
                First Installment Start Date <span className="text-destructive">*</span>
              </Label>
              <DatePartsPicker
                value={draft.startDate}
                onChange={(iso) => setDraft((x) => ({ ...x, startDate: iso }))}
                fromYear={1980}
                toYear={new Date().getFullYear() + 5}
                placeholder="Select start date"
              />
            </div>
          </div>

          {/* Right Column: Live Repayment Calculation & Preview Card */}
          <div className="flex flex-col justify-between rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-4">
            <div>
              <div className="flex items-center gap-2 pb-3 border-b border-primary/10">
                <Calculator className="w-4 h-4 text-primary" />
                <h4 className="font-bold text-xs uppercase tracking-wider text-foreground">Live Schedule Preview</h4>
              </div>

              <div className="space-y-3 pt-3 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Sanctioned Amount</span>
                  <span className="font-bold text-foreground text-sm">{loanNum ? formatCurrency(loanNum) : "—"}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Monthly Deduction</span>
                  <span className="font-bold text-rose-600">{instNum ? `-${formatCurrency(instNum)}` : "—"}</span>
                </div>
                <div className="flex justify-between items-center border-t border-primary/10 pt-2">
                  <span className="text-muted-foreground">Total Installments</span>
                  <span className="font-extrabold text-primary text-sm">
                    {installmentsCount ? `${installmentsCount} Months` : "—"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Estimated Payoff</span>
                  <span className="font-bold text-foreground">{completionDateFormatted || "—"}</span>
                </div>

                {/* Progress bar visual indicator */}
                {installmentsCount && loanNum && instNum ? (
                  <div className="bg-background/80 rounded-lg border border-primary/20 p-3 space-y-2 mt-2">
                    <div className="flex justify-between text-[11px] font-bold">
                      <span className="text-muted-foreground">Monthly Deduction Impact</span>
                      <span className="text-primary">{((instNum / (basicSalaryNum || loanNum)) * 100).toFixed(1)}% of basic</span>
                    </div>
                    <div className="h-2 w-full bg-primary/20 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${Math.min(100, Math.round((instNum / (basicSalaryNum || loanNum)) * 100))}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground italic text-center pt-0.5">
                      Deducted automatically from monthly salary payslips starting {format(parseISO(draft.startDate), "MMM yyyy")}.
                    </p>
                  </div>
                ) : (
                  <div className="bg-background/50 rounded-lg border border-border/50 p-3 text-center text-xs text-muted-foreground italic">
                    Enter loan and installment amounts to view live schedule breakdown.
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground pt-2 border-t border-primary/10">
              <Info className="w-3.5 h-3.5 text-primary shrink-0" />
              <span>Loan balance updates automatically as payroll batches are finalized.</span>
            </div>
          </div>
        </div>

        <DialogFooter className="pt-4 border-t border-border/50 gap-2">
          <Button variant="outline" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => void save()}
            disabled={saving || !employeeId || !loanNum || !instNum}
            className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sanctioning Loan...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Sanction Loan
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
